/**
 * The permission catalogue, mirrored from `backend/app/core/permissions.py`.
 *
 * The backend serves the full catalogue with labels and descriptions at
 * `/roles/permissions` — that is what Settings renders. This mirror exists so
 * route guards and conditional UI can reference permissions as constants the
 * compiler checks, instead of loose strings scattered through components.
 */

export const Permission = {
  PANEL_ACCESS: 'panel.access',

  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_VIEW_FINANCIALS: 'dashboard.view_financials',

  SETTINGS_VIEW: 'settings.view',
  SETTINGS_MANAGE: 'settings.manage',
  SETTINGS_MANAGE_PERMISSIONS: 'settings.manage_permissions',

  RULES_VIEW: 'rules.view',
  RULES_EDIT: 'rules.edit',
  RULES_PUBLISH: 'rules.publish',

  STAFF_RULES_EDIT: 'staff_rules.edit',
  STAFF_RULES_PUBLISH: 'staff_rules.publish',

  TICKETS_VIEW: 'tickets.view',
  TICKETS_VIEW_ALL: 'tickets.view_all',
  TICKETS_CLAIM: 'tickets.claim',
  TICKETS_REPLY: 'tickets.reply',
  TICKETS_CLOSE: 'tickets.close',
  TICKETS_REOPEN: 'tickets.reopen',
  TICKETS_DELETE: 'tickets.delete',
  TICKETS_MANAGE_CATEGORIES: 'tickets.manage_categories',
  TICKETS_MANAGE_FORMS: 'tickets.manage_forms',
  TICKETS_MANAGE_PANELS: 'tickets.manage_panels',

  BUGS_VIEW: 'bugs.view',
  BUGS_MANAGE: 'bugs.manage',

  ROADMAP_VIEW: 'roadmap.view',
  ROADMAP_MANAGE: 'roadmap.manage',
  STATUS_PAGE_VIEW: 'statuspage.view',
  STATUS_PAGE_MANAGE: 'statuspage.manage',
  TESTERS_VIEW: 'testers.view',
  TESTERS_MANAGE: 'testers.manage',
  WIKI_EDIT: 'wiki.edit',
  WIKI_MANAGE: 'wiki.manage',

  WHITELIST_VIEW: 'whitelist.view',
  WHITELIST_DECIDE: 'whitelist.decide',
  WHITELIST_REQUEST_CHECK: 'whitelist.request_check',
  WHITELIST_VIEW_REPORTS: 'whitelist.view_reports',
  WHITELIST_REVOKE: 'whitelist.revoke',
  WHITELIST_MANAGE: 'whitelist.manage',

  APPLICATIONS_VIEW: 'applications.view',
  APPLICATIONS_DECIDE: 'applications.decide',
  APPLICATIONS_MANAGE: 'applications.manage',

  GIVEAWAYS_VIEW: 'giveaways.view',
  GIVEAWAYS_MANAGE: 'giveaways.manage',
  GIVEAWAYS_DRAW: 'giveaways.draw',

  POLLS_VIEW: 'polls.view',
  POLLS_MANAGE: 'polls.manage',
  POLLS_PUBLISH: 'polls.publish',

  VEHICLES_VIEW: 'vehicles.view',
  VEHICLES_MANAGE: 'vehicles.manage',
  VEHICLES_TUNE: 'vehicles.tune',
  VEHICLES_EXPORT: 'vehicles.export',
  VEHICLES_MANAGE_PANEL: 'vehicles.manage_panel',
  GARAGES_VIEW: 'garages.view',
  GARAGES_MANAGE: 'garages.manage',

  RENTALS_VIEW: 'rentals.view',
  RENTALS_MANAGE: 'rentals.manage',
  CARWASH_VIEW: 'carwash.view',
  CARWASH_MANAGE: 'carwash.manage',
  PLATES_VIEW: 'plates.view',
  PLATES_MANAGE: 'plates.manage',

  WEAPONS_VIEW: 'weapons.view',
  WEAPONS_TUNE: 'weapons.tune',
  WEAPONS_EXPORT: 'weapons.export',

  CHARACTERS_VIEW: 'characters.view',
  CHARACTERS_MANAGE: 'characters.manage',
  CHARACTERS_DELETE: 'characters.delete',

  CHAT_VIEW: 'chat.view',
  CHAT_MODERATE: 'chat.moderate',
  CHAT_SEND: 'chat.send',

  LIVE_MAP_VIEW: 'live_map.view',
  LIVE_MAP_SCREEN: 'live_map.screen',

  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_IMAGES: 'inventory.images',
  INVENTORY_MANAGE: 'inventory.manage',
  INVENTORY_CATALOGUE: 'inventory.catalogue',

  BANKING_VIEW: 'banking.view',
  BANKING_MANAGE: 'banking.manage',
  BANKING_MONEY: 'banking.money',
  BANKING_ANNOTATE: 'banking.annotate',

  DOMAINS_VIEW: 'domains.view',
  DOMAINS_MANAGE: 'domains.manage',

  PHONES_VIEW: 'phones.view',
  PHONES_MEDIA: 'phones.media',
  PHONES_MESSAGES: 'phones.messages',
  PHONES_LISTEN: 'phones.listen',

  TABLETS_VIEW: 'tablets.view',
  TABLETS_MEDIA: 'tablets.media',
  TABLETS_CONTENT: 'tablets.content',

  MAIL_VIEW: 'mail.view',
  MAIL_READ: 'mail.read',
  MAIL_MANAGE: 'mail.manage',

  CCTV_VIEW: 'cctv.view',
  SPEEDCAMERAS_VIEW: 'speedcameras.view',

  GRAFFITI_VIEW: 'graffiti.view',
  GRAFFITI_MANAGE: 'graffiti.manage',
  LEDPANELS_VIEW: 'ledpanels.view',
  LEDPANELS_MANAGE: 'ledpanels.manage',
  PINGPONG_VIEW: 'pingpong.view',
  PINGPONG_MANAGE: 'pingpong.manage',
  DRUGS_VIEW: 'drugs.view',
  DRUGS_MANAGE: 'drugs.manage',
  RACES_VIEW: 'races.view',
  RACES_MANAGE: 'races.manage',
  EMOTES_VIEW: 'emotes.view',
  EMOTES_MANAGE: 'emotes.manage',
  SCOOTERS_VIEW: 'scooters.view',
  SCOOTERS_MANAGE: 'scooters.manage',
  STYLE_VIEW: 'style.view',
  STYLE_MANAGE: 'style.manage',
  VENDING_VIEW: 'vending.view',
  VENDING_MANAGE: 'vending.manage',
  SHOPS_VIEW: 'shops.view',
  SHOPS_MANAGE: 'shops.manage',
  CRAFTING_VIEW: 'crafting.view',
  CRAFTING_MANAGE: 'crafting.manage',
  CHESS_VIEW: 'chess.view',
  CHESS_MANAGE: 'chess.manage',
  BILLIARDS_VIEW: 'billiards.view',
  BILLIARDS_MANAGE: 'billiards.manage',
  COMPUTERS_VIEW: 'computers.view',
  COMPUTERS_MANAGE: 'computers.manage',
  DIRECTORY_VIEW: 'directory.view',
  DIRECTORY_MANAGE: 'directory.manage',
  DATACENTER_VIEW: 'datacenter.view',
  DATACENTER_MANAGE: 'datacenter.manage',
  GROW_VIEW: 'grow.view',
  GROW_MANAGE: 'grow.manage',

  NEWS_VIEW: 'news.view',
  NEWS_MANAGE: 'news.manage',

  INSTASHOT_VIEW: 'instashot.view',
  INSTASHOT_MANAGE: 'instashot.manage',
  INSTASHOT_MESSAGES: 'instashot.messages',

  CHIRP_VIEW: 'chirp.view',
  CHIRP_MANAGE: 'chirp.manage',
  CHIRP_MESSAGES: 'chirp.messages',

  STATIONS_VIEW: 'stations.view',
  STATIONS_MANAGE: 'stations.manage',

  DOORS_VIEW: 'doors.view',
  DOORS_MANAGE: 'doors.manage',

  DRIVINGSCHOOL_VIEW: 'drivingschool.view',
  DRIVINGSCHOOL_MANAGE: 'drivingschool.manage',

  FIREARMS_VIEW: 'firearms.view',
  FIREARMS_MANAGE: 'firearms.manage',
  INSURANCE_VIEW: 'insurance.view',
  INSURANCE_MANAGE: 'insurance.manage',
  PAWN_VIEW: 'pawn.view',
  PAWN_MANAGE: 'pawn.manage',

  IMPOUND_VIEW: 'impound.view',
  IMPOUND_MANAGE: 'impound.manage',

  MDT_VIEW: 'mdt.view',
  MDT_MANAGE: 'mdt.manage',

  DISPATCH_VIEW: 'dispatch.view',
  DISPATCH_OPERATE: 'dispatch.operate',

  MEDICAL_VIEW: 'medical.view',
  MEDICAL_MANAGE: 'medical.manage',

  JAIL_VIEW: 'jail.view',
  JAIL_MANAGE: 'jail.manage',

  ROBBERIES_VIEW: 'robberies.view',
  ROBBERIES_MANAGE: 'robberies.manage',
  ROBBERIES_SETTINGS: 'robberies.settings',

  NETWORK_VIEW: 'network.view',

  POWER_VIEW: 'power.view',
  POWER_MANAGE: 'power.manage',

  QUESTS_VIEW: 'quests.view',
  QUESTS_MANAGE: 'quests.manage',
  QUESTS_ASSIGN: 'quests.assign',

  STATUS_VIEW: 'status.view',
  STATUS_MANAGE: 'status.manage',
  STATUS_ADJUST: 'status.adjust',

  SKILLS_VIEW: 'skills.view',
  SKILLS_MANAGE: 'skills.manage',
  SKILLS_ADJUST: 'skills.adjust',

  TALENTS_VIEW: 'talents.view',
  TALENTS_MANAGE: 'talents.manage',
  TALENTS_ADJUST: 'talents.adjust',

  WEATHER_VIEW: 'weather.view',
  WEATHER_MANAGE: 'weather.manage',

  NPC_VIEW: 'npc.view',
  NPC_MANAGE: 'npc.manage',

  BATTLEPASS_VIEW: 'battlepass.view',
  BATTLEPASS_MANAGE: 'battlepass.manage',
  BATTLEPASS_GRANT: 'battlepass.grant',

  BUSINESSES_VIEW: 'businesses.view',
  BUSINESSES_MANAGE: 'businesses.manage',
  BUSINESSES_MONEY: 'businesses.money',

  HOUSING_VIEW: 'housing.view',
  HOUSING_MANAGE: 'housing.manage',
  SHELLS_VIEW: 'shells.view',
  SHELLS_MANAGE: 'shells.manage',

  MEMBERS_VIEW: 'members.view',
  MEMBERS_MANAGE_ROLES: 'members.manage_roles',
  MEMBERS_RESTORE_ROLES: 'members.restore_roles',
  MEMBERS_KICK: 'members.kick',
  MEMBERS_BAN: 'members.ban',

  MODERATION_VIEW: 'moderation.view',
  MODERATION_ACT: 'moderation.act',
  MODERATION_MANAGE_RULES: 'moderation.manage_rules',
  MODERATION_MANAGE_AI: 'moderation.manage_ai',

  DISCIPLINE_VIEW: 'discipline.view',
  DISCIPLINE_ISSUE: 'discipline.issue',
  DISCIPLINE_SANCTION: 'discipline.sanction',
  DISCIPLINE_REVOKE: 'discipline.revoke',
  DISCIPLINE_APPEALS: 'discipline.appeals',
  DISCIPLINE_MANAGE: 'discipline.manage',

  VOICE_VIEW: 'voice.view',
  VOICE_MANAGE_HUBS: 'voice.manage_hubs',
  VOICE_MANAGE_CHANNELS: 'voice.manage_channels',
  VOICE_MANAGE_STATS: 'voice.manage_stats',

  STAFF_MANAGE_PANEL: 'staff.manage_panel',

  ABSENCES_VIEW: 'absences.view',
  ABSENCES_MANAGE: 'absences.manage',
  ABSENCES_MANAGE_PANEL: 'absences.manage_panel',

  MESSAGES_VIEW: 'messages.view',
  MESSAGES_MANAGE: 'messages.manage',

  BOT_VIEW: 'bot.view',
  BOT_MANAGE: 'bot.manage',
  BOT_BROADCAST: 'bot.broadcast',
  BOT_ARCHITECT: 'bot.architect',

  AUDIT_VIEW: 'audit.view',
  AUDIT_EXPORT: 'audit.export',

  GAME_VIEW: 'game.view',
  GAME_ACT: 'game.act',
  GAME_MANAGE: 'game.manage',
} as const

export type PermissionKey = (typeof Permission)[keyof typeof Permission]
